import { DateUtils, PersonDoc, WorkflowState, WorkflowStateComplete } from 'common';
import { TimelineBubble } from '../models/Timeline';
import { Calendar } from '../../components';
import { h } from '@stencil/core';

/**
 * Construction of TimelineBubble presentation-model objects for the timeline UI.
 * Shared by both submittals and RFIs.
 */
export class TimelineUtils {
  /**
   * Due date for a pending workflow state, measured as working days from an anchor date.
   *
   * @param states - Full workflow states array
   * @param pendingFromIndex - Index of the first pending state (last completed index + 1)
   * @param pendingStateIndex - 0-based index within the pending states slice
   * @param anchorDate - Date to measure from (last history date, or today for drafts)
   * @param calendar - Project calendar for working-day calculations
   */
  static getPendingStateDueDate(
    states: WorkflowState[],
    pendingFromIndex: number,
    pendingStateIndex: number,
    anchorDate: Date,
    calendar: Calendar,
  ): Date {
    const cumulativeDays = states
      .slice(pendingFromIndex, pendingFromIndex + pendingStateIndex + 1)
      .reduce((sum, state) => sum + (state.dueDays || 0), 0);
    const durationInSeconds = calendar.workingDaysToSeconds(cumulativeDays);
    return calendar.getDateFromPositiveDuration(anchorDate, durationInSeconds);
  }

  /**
   * Due date at the end of all remaining workflow states from an anchor date.
   * Returns null when there are no pending states.
   */
  static getWorkflowEndDueDate(
    states: WorkflowState[],
    pendingFromIndex: number,
    anchorDate: Date,
    calendar: Calendar,
  ): Date | null {
    const pendingStates = states.slice(pendingFromIndex);
    if (pendingStates.length === 0) return null;
    return TimelineUtils.getPendingStateDueDate(states, pendingFromIndex, pendingStates.length - 1, anchorDate, calendar);
  }

  /**
   * Builds timeline bubbles from a raw history array, workflow states, people, and a calendar.
   * Used by submittals and RFIs directly.
   *
   * @param history - The WorkflowStateComplete[] status history array
   * @param states - The workflow states for this document
   * @param people - People to resolve user_id references
   * @param calendar - Calendar instance for computing working-day due dates on pending states
   * @param createStatusId - The fallback id to use when a reversion history item has a null state_id
   */
  static getTimelineBubbles(
    history: WorkflowStateComplete[],
    states: WorkflowState[],
    people: PersonDoc[],
    calendar: Calendar,
    createStatusId: string = 'created',
  ): TimelineBubble[] {
    const lastHistoryItem = history.length > 0 ? history[history.length - 1] : null;
    const lastStateIndex = states.findIndex(s => s.id === lastHistoryItem?.state_id);

    // --- Completed (history) bubbles ---
    const historyBubbles: TimelineBubble[] = history.map(historyItem => {
      const doneDate = DateUtils.fromISO(historyItem.date);
      const user = people.find(p => p._id === historyItem.user_id);

      if (historyItem.previous_state_id) {
        // Reversion bubble: workflow was sent back to a prior state
        const prevState = states.find(s => s.id === historyItem.previous_state_id);
        const returnStateId = historyItem.state_id === null ? createStatusId : historyItem.state_id;
        const returnBubble: TimelineBubble = {
          id: returnStateId,
          dueDate: doneDate,
          doneDate: doneDate,
          people: user ? [user] : [],
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {prevState?.action}
              <ion-icon name="arrow-back-outline"></ion-icon>
            </div>
          ),
        };
        return returnBubble;
      }

      const stateIndex = states.findIndex(s => s.id === historyItem.state_id);
      const currentState = states[stateIndex];

      const bubble: TimelineBubble = {
        id: historyItem.state_id,
        dueDate: doneDate,
        doneDate: doneDate,
        people: user ? [user] : [],
        label: currentState?.pastTense ?? currentState?.action ?? '',
      };
      return bubble;
    });

    // --- Pending (future) bubbles, using working-day calendar ---
    const lastHistoryDate = DateUtils.fromISO(lastHistoryItem?.date ?? new Date().toISOString());
    const pendingFromIndex = lastStateIndex + 1;
    const pendingBubbles: TimelineBubble[] = states.slice(pendingFromIndex).map((state, index) => {
      const users = people.filter(p => state.dueBy?.includes(p._id));
      const dueDate = TimelineUtils.getPendingStateDueDate(states, pendingFromIndex, index, lastHistoryDate, calendar);
      return {
        id: state.id,
        dueDate,
        people: users || [],
        label: state.action,
      };
    });

    return [...historyBubbles, ...pendingBubbles];
  }
}
